/**
 * TILE FINDER QUIZ — Direct Tile Warehouse
 * Version 1.0 • May 2026
 *
 * Multi-step quiz engine with conditional logic, progress tracking,
 * Shopify Storefront API filtering, and shareable URLs.
 */

(function () {
  'use strict';

  /* ================================================================
     1. QUIZ DATA — Questions, Options, Conditions, Attribute Maps
     ================================================================ */

  const QUIZ_CONFIG = {
    maxColours: 3,
    storeDomain: window.Shopify?.shop || window.location.hostname,
    storefrontToken: '', // Set via section settings or data attribute
    storefrontApiVersion: '2026-04', // Shopify Storefront API version (keep on a supported/stable version)
    contactUrl: '/contact/',
    resultsPerPage: 250, // upper bound on results shown (grid paginates 9 at a time via "load more")
    emailPublicKey: '', // EmailJS — set via section settings
    emailServiceId: '',
    emailTemplateId: '',
    maxEmailRecommended: 8, // cap tiles in the email to keep size reasonable
    maxEmailWorthConsidering: 4,
  };

  // Neutral inline placeholder (no external dependency). Used for option
  // thumbnails until the merchant sets real images in the Theme Customizer,
  // and for product cards whose product has no image.
  const PLACEHOLDER_IMG = 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22200%22%20height%3D%22200%22%20viewBox%3D%220%200%20200%20200%22%3E%3Crect%20width%3D%22200%22%20height%3D%22200%22%20fill%3D%22%23F2EEF1%22%2F%3E%3Ccircle%20cx%3D%2274%22%20cy%3D%2272%22%20r%3D%2213%22%20fill%3D%22%23D7A8CE%22%2F%3E%3Cpath%20d%3D%22M40%20150l34-40%2024%2028%2020-22%2042%2034z%22%20fill%3D%22%23D7A8CE%22%2F%3E%3C%2Fsvg%3E';

  const QUESTIONS = [
    {
      id: 'q1',
      key: 'area',
      title: 'What area are you decorating?',
      desc: 'Select the room or space where you plan to install the tiles.',
      type: 'single',
      image: 'q1', // image key — mapped in section settings
      options: [
        { value: 'bathroom', label: 'Bathroom' },
        { value: 'kitchen', label: 'Kitchen' },
        { value: 'hallway', label: 'Hallway' },
        { value: 'living-room', label: 'Living Room' },
        { value: 'conservatory', label: 'Conservatory' },
        { value: 'outside-garden', label: 'Outside / Garden' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      id: 'q5',
      key: 'surface',
      title: 'Are you looking for Wall or Floor?',
      desc: 'Select where you plan to install the tiles.',
      type: 'single',
      image: 'q5',
      options: [
        { value: 'wall', label: 'Wall' },
        { value: 'floor', label: 'Floor' },
        { value: 'both', label: 'Both - Matching/Same Tiles' },
      ],
    },
    {
      id: 'q2',
      key: 'pattern',
      title: 'What kind of patterns do you like?',
      desc: 'Choose the pattern style that appeals to you most.',
      type: 'single',
      image: 'q2',
      options: [
        { value: 'plain', label: 'Plain', img: PLACEHOLDER_IMG },
        { value: 'marble-effect', label: 'Marble / Onyx Effect', img: PLACEHOLDER_IMG },
        { value: 'wood-effect', label: 'Wood Effect', img: PLACEHOLDER_IMG },
        { value: 'concrete-effect', label: 'Concrete Effect', img: PLACEHOLDER_IMG },
        { value: 'split-face', label: 'Split Face / Brick', img: PLACEHOLDER_IMG },
        { value: 'subtle-pattern', label: 'Subtle Patterns', img: PLACEHOLDER_IMG },
        { value: 'strong-pattern', label: 'Strong Patterns', img: PLACEHOLDER_IMG },
        { value: 'no-preference', label: 'No Preference', img: PLACEHOLDER_IMG },
      ],
    },
    {
      id: 'q3',
      key: 'colour',
      title: 'What colour(s) are you looking for?',
      desc: 'Select up to 3 colours that match your vision.',
      type: 'multi',
      maxSelect: 3,
      image: 'q3',
      options: [
        { value: 'white', label: 'White', swatch: 'white' },
        { value: 'beige-cream', label: 'Beige / Cream', swatch: 'beige-cream' },
        { value: 'grey', label: 'Grey', swatch: 'grey' },
        { value: 'red', label: 'Red', swatch: 'red' },
        { value: 'green', label: 'Green', swatch: 'green' },
        { value: 'blue', label: 'Blue', swatch: 'blue' },
        { value: 'pink', label: 'Pink', swatch: 'pink' },
        { value: 'brown', label: 'Brown', swatch: 'brown' },
        { value: 'black', label: 'Black', swatch: 'black' },
        { value: 'yellow', label: 'Yellow', swatch: 'yellow' },
        { value: 'orange', label: 'Orange', swatch: 'orange' },
        { value: 'multicoloured', label: 'Multicoloured', swatch: 'multicoloured' },
      ],
    },
    {
      id: 'q4',
      key: 'look',
      title: 'What kind of look do you want?',
      desc: 'This affects the tile size — larger tiles mean fewer grout lines.',
      type: 'single',
      image: 'q4',
      options: [
        { value: 'large-rectified', label: 'Minimal Grout Lines', sublabel: 'Large & Rectified Tiles', img: PLACEHOLDER_IMG },
        { value: 'medium', label: 'Some Grout Lines', sublabel: 'Medium Tiles', img: PLACEHOLDER_IMG },
        { value: 'small', label: 'Lots of Grout Lines', sublabel: 'Small Tiles', img: PLACEHOLDER_IMG },
        { value: 'mosaic', label: 'Mosaic Look', sublabel: 'Mosaic', img: PLACEHOLDER_IMG },
        { value: 'no-preference', label: 'No Preference', img: PLACEHOLDER_IMG },
      ],
    },
    {
      id: 'q6',
      key: 'finish',
      title: 'Do you prefer Glossy or Matt Finish?',
      desc: 'Glossy tiles reflect light; matt tiles have a softer, modern look.',
      type: 'single',
      image: 'q6',
      options: [
        { value: 'glossy', label: 'Glossy - easy to clean and reflects light' },
        { value: 'matt', label: 'Matt - modern finish, hides marks and imperfections' },
        { value: 'no-preference', label: 'No Preference' },
      ],
    },
    {
      id: 'q7',
      key: 'antislip',
      title: 'What level of Anti-Slip do you want?',
      desc: 'Higher anti-slip ratings are recommended for wet areas.',
      type: 'single',
      image: 'q7',
      condition: (answers) => answers.q5 !== 'wall' && answers.q6 !== 'glossy',
      options: [
        { value: 'very-high', label: 'Very High', sublabel: 'Pool areas' },
        { value: 'high', label: 'High', sublabel: 'Wet room / bathroom' },
        { value: 'medium', label: 'Medium', sublabel: 'Kitchen / bathroom' },
        { value: 'standard', label: 'Standard', sublabel: 'Hallway / living room' },
        { value: 'not-sure', label: 'Not sure' },
      ],
    },
    {
      id: 'q8',
      key: 'ufh',
      title: 'Do you / will you have underfloor heating?',
      desc: 'Some tiles are specifically rated for underfloor heating systems.',
      type: 'single',
      image: 'q8',
      condition: (answers) => answers.q1 !== 'outside-garden' && answers.q5 !== 'wall',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      id: 'q9',
      key: 'shape',
      title: 'What shape tiles do you want?',
      desc: 'Choose the tile shape that fits your design.',
      type: 'single',
      image: 'q9',
      options: [
        { value: 'rectangle', label: 'Rectangle' },
        { value: 'square', label: 'Square' },
        { value: 'hexagonal', label: 'Hexagonal' },
        { value: 'other', label: 'Other' },
        { value: 'no-preference', label: 'No Preference' },
      ],
    },
    {
      id: 'q10',
      key: 'budget',
      title: "What's your Budget?",
      desc: 'Select a price range per square metre.',
      type: 'single',
      image: 'q10',
      options: [
        { value: 'under-20', label: 'Up to £20/m²' },
        { value: '20-30', label: '£20–30/m²' },
        { value: '30-plus', label: '£30+/m²' },
        { value: 'no-preference', label: 'No Preference' },
      ],
    },
  ];

  /**
   * Maps quiz answer values to Shopify tag/metafield queries.
   * NOTE: These are INDICATIVE — must be confirmed with in-house Shopify dev.
   */
  const ATTRIBUTE_MAP = {
    q1: { type: 'tag', prefix: 'area-' },
    q2: { type: 'tag', prefix: 'pattern-', skip: ['no-preference'] },
    q3: { type: 'tag', prefix: 'colour-', logic: 'OR' },
    q4: { type: 'tag', prefix: 'size-', skip: ['no-preference'] },
    q5: { type: 'tag', prefix: 'surface-' },
    q6: { type: 'tag', prefix: 'finish-', skip: ['no-preference'] },
    q7: { type: 'tag', prefix: 'antislip-', skip: ['not-sure'] },
    q8: { type: 'metafield', namespace: 'custom', key: 'underfloor_heating', mapValues: { yes: 'true', no: 'false' } },
    q9: { type: 'tag', prefix: 'shape-', skip: ['no-preference'] },
    q10: { type: 'tag', prefix: 'budget-', skip: ['no-preference'] },
  };

  // Filter relaxation priority — dropped one at a time, cheapest preference first,
  // until something matches. This MUST cover every preference question: anything left
  // out can never be relaxed, so a shopper whose binding constraint is that question
  // dead-ends on "0 Tiles Matched" with no way forward. Colour and surface were the
  // original omission — e.g. kitchen + pink + wall-and-floor matches nothing in the
  // catalogue, because the few pink tiles are all wall-only.
  //
  // q7 (anti-slip) and q8 (underfloor heating) are intentionally absent: they are
  // functional requirements, not preferences, and relaxing them would recommend a
  // tile that genuinely does not work for the shopper's job.
  const RELAXATION_ORDER = ['q9', 'q2', 'q6', 'q4', 'q10', 'q5', 'q3', 'q1'];

  /* ================================================================
     2. STATE
     ================================================================ */

  let state = {
    screen: 'intro', // 'intro' | 'quiz' | 'results'
    currentStep: 0,
    answers: {},
    products: [],
    relaxedFilters: [],
    isLoading: false,
    prevProgress: 0, // tracks previous % for animation
    productQuantities: {},
  };

  /* ================================================================
     3. DOM HELPERS
     ================================================================ */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === 'className') node.className = v;
      else if (k === 'innerHTML') node.innerHTML = v;
      else if (k === 'textContent') node.textContent = v;
      else if (k.startsWith('on')) node.addEventListener(k.slice(2).toLowerCase(), v);
      else node.setAttribute(k, v);
    }
    for (const c of children) {
      if (typeof c === 'string') node.appendChild(document.createTextNode(c));
      else if (c) node.appendChild(c);
    }
    return node;
  }

  function getProductKey(product) {
    return product?.id || product?.handle || product?.title || '';
  }

  /* ================================================================
     4. QUIZ ENGINE
     ================================================================ */

  function getVisibleQuestions() {
    return QUESTIONS.filter(q => !q.condition || q.condition(state.answers));
  }

  function getCurrentQuestion() {
    const visible = getVisibleQuestions();
    return visible[state.currentStep] || null;
  }

  function getProgressPercent() {
    const visible = getVisibleQuestions();
    if (!visible.length) return 0;
    return Math.round(((state.currentStep) / visible.length) * 100);
  }

  function getPreviousAnswerSummary() {
    const visible = getVisibleQuestions();
    if (state.currentStep === 0) return '';
    const prevQ = visible[state.currentStep - 1];
    if (!prevQ) return '';
    const ans = state.answers[prevQ.id];
    if (!ans) return '';
    const labels = Array.isArray(ans)
      ? ans.map(v => prevQ.options.find(o => o.value === v)?.label || v).join(', ')
      : prevQ.options.find(o => o.value === ans)?.label || ans;
    return `${prevQ.key.charAt(0).toUpperCase() + prevQ.key.slice(1)}: <span>${labels}</span>`;
  }

  function isCurrentAnswered() {
    const q = getCurrentQuestion();
    if (!q) return false;
    const ans = state.answers[q.id];
    if (q.type === 'multi') return Array.isArray(ans) && ans.length > 0;
    return !!ans;
  }

  function clearDependentAnswers(changedQuestionId) {
    // If a prior answer changed that affects conditional questions, clear those
    const changedIdx = QUESTIONS.findIndex(q => q.id === changedQuestionId);
    QUESTIONS.forEach((q, idx) => {
      if (idx > changedIdx && q.condition && !q.condition(state.answers)) {
        delete state.answers[q.id];
      }
    });
  }

  /* ================================================================
     5. RENDERING
     ================================================================ */

  let root;

  // Override the default per-option thumbnails (Q2 Pattern, Q4 Look) with images
  // set by the merchant in the Theme Customizer. The section emits a JSON map in
  // #tfq-option-images; any entry with a non-empty URL replaces the built-in default.
  function applyOptionImageOverrides() {
    const node = document.getElementById('tfq-option-images');
    if (!node) return;
    let map;
    try {
      map = JSON.parse(node.textContent);
    } catch (e) {
      // A malformed blob drops every override at once, so the quiz silently falls back
      // to placeholders for all options. Say so — this failed unnoticed in UAT.
      if (window.console) {
        console.warn('[Tile Finder] Could not parse #tfq-option-images; falling back to ' +
          'built-in option thumbnails. Check the section for a Liquid error.', e);
      }
      return;
    }
    QUESTIONS.forEach((q) => {
      const overrides = map[q.id];
      if (!overrides || !q.options) return;
      q.options.forEach((opt) => {
        const url = overrides[opt.value];
        if (url) opt.img = url;
      });
    });
  }

  function init() {
    root = $('.tfq');
    if (!root) return;

    // Read config from data attributes
    const token = root.dataset.storefrontToken;
    if (token) QUIZ_CONFIG.storefrontToken = token;

    const storeDomain = root.dataset.storeDomain;
    if (storeDomain) QUIZ_CONFIG.storeDomain = storeDomain;

    const contactUrl = root.dataset.contactUrl;
    if (contactUrl) QUIZ_CONFIG.contactUrl = contactUrl;

    // EmailJS config
    QUIZ_CONFIG.emailPublicKey = root.dataset.emailjsPublicKey || '';
    QUIZ_CONFIG.emailServiceId = root.dataset.emailjsServiceId || '';
    QUIZ_CONFIG.emailTemplateId = root.dataset.emailjsTemplateId || '';

    // Initialise EmailJS once the SDK is available
    if (QUIZ_CONFIG.emailPublicKey && window.emailjs) {
      try {
        window.emailjs.init({ publicKey: QUIZ_CONFIG.emailPublicKey });
      } catch (e) {
        // Silent fail — handled at send time
      }
    }

    // Apply per-option thumbnail images set in the Theme Customizer (Q2/Q4)
    applyOptionImageOverrides();

    // Check URL params for shared quiz state
    if (restoreFromURL()) {
      showLoadingAndFetchResults();
      return;
    }

    // Check sessionStorage for in-progress quiz
    restoreFromSession();

    if (state.screen === 'intro') {
      renderIntro();
    } else if (state.screen === 'quiz') {
      renderQuiz();
    }
  }

  // ---------- Intro ----------
  function renderIntro() {
    state.screen = 'intro';
    const introEl = $('.tfq__intro', root);
    const quizEl = $('.tfq__quiz-screen', root);
    const resultsEl = $('.tfq__results', root);

    const howEl = $('.tfq__how', root);

    if (introEl) introEl.style.display = '';
    if (howEl) howEl.style.display = '';
    if (quizEl) quizEl.classList.remove('is-active');
    if (resultsEl) resultsEl.classList.remove('is-active');
  }

  function startQuiz() {
    state.screen = 'quiz';
    state.currentStep = 0;
    state.answers = {};
    lastMatchCount = null; // Reset match counter

    const introEl = $('.tfq__intro', root);
    const howEl = $('.tfq__how', root);
    if (introEl) introEl.style.display = 'none';
    if (howEl) howEl.style.display = 'none';

    // Preload all quiz images immediately
    preloadQuizImages();

    renderQuiz();
    saveToSession();
    trackEvent('tile_quiz_start', {});
  }

  function preloadQuizImages() {
    const imgContainer = $('.tfq__quiz-image', root);
    if (!imgContainer) return;
    const imgs = $$('img[data-question-image]', imgContainer);
    imgs.forEach(img => {
      if (img.loading === 'lazy') img.loading = 'eager';
      // Force browser to start loading by reading src
      const src = img.getAttribute('src');
      if (src) {
        const preload = new Image();
        preload.src = src;
      }
    });
  }

  // ---------- Quiz ----------

  // Scaffold is built once; updateQuizContent swaps inner content dynamically
  let quizScaffoldBuilt = false;

  // Circumference for SVG ring: 2π × r where r=31 (desktop) or r=26 (mobile)
  const RING_CIRCUMFERENCE_DESKTOP = 2 * Math.PI * 31; // ≈ 194.78
  const RING_CIRCUMFERENCE_MOBILE = 2 * Math.PI * 26; // ≈ 163.36
  
  function isMobile() {
    return window.innerWidth <= 768;
  }

  function ensureQuizScaffold() {
    const quizEl = $('.tfq__quiz-screen', root);
    const panel = $('.tfq__quiz-panel', quizEl);
    if (!panel || quizScaffoldBuilt) return;
    
    // Fixed ring geometry in a fixed viewBox; the SVG scales to its container via CSS
    // (68px desktop, 56px mobile), so the ring and its centered % label always stay
    // aligned even if the width changes after the scaffold is built.
    const size = 68;
    const center = 34;
    const radius = 31;
    const circumference = RING_CIRCUMFERENCE_DESKTOP;

    panel.innerHTML = `
      <div class="tfq__question-content">
        <!-- Breadcrumb trail sits above the question title -->
        <div class="tfq__progress-trail"></div>
        <h2 class="tfq__question-title"></h2>
        <p class="tfq__question-desc"></p>
        <div class="tfq__colour-counter tfq__hidden"></div>
        <div class="tfq__options"></div>
      </div>
      <div class="tfq__progress">
        <div class="tfq__progress-ring-wrap">
          <div class="tfq__progress-ring-container">
            <svg class="tfq__progress-ring" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
              <circle class="tfq__progress-ring-track" cx="${center}" cy="${center}" r="${radius}"/>
              <circle class="tfq__progress-ring-fill" cx="${center}" cy="${center}" r="${radius}"
                stroke-dasharray="${circumference.toFixed(2)}"
                stroke-dashoffset="${circumference.toFixed(2)}"/>
            </svg>
            <div class="tfq__progress-ring-label">
              <strong class="tfq__progress-pct">0%</strong>
              <span>done</span>
            </div>
          </div>
          <div class="tfq__progress-info">
            <span class="tfq__progress-step-label">Step 1 of 1</span>
          </div>
        </div>
        <div class="tfq__nav"></div>
      </div>
    `;
    quizScaffoldBuilt = true;
  }

  function renderQuiz() {
    state.screen = 'quiz';
    const quizEl = $('.tfq__quiz-screen', root);
    const introEl = $('.tfq__intro', root);
    const resultsEl = $('.tfq__results', root);
    const howEl = $('.tfq__how', root);

    if (introEl) introEl.style.display = 'none';
    if (howEl) howEl.style.display = 'none';
    if (resultsEl) resultsEl.classList.remove('is-active');
    if (quizEl) quizEl.classList.add('is-active');

    const q = getCurrentQuestion();
    if (!q) return;

    ensureQuizScaffold();
    updateQuizContent(q);
    updateQuizImage(q);

    // Re-anchor the viewport to the question on every step change — otherwise the
    // page keeps its scroll position and can land on the footer (esp. on mobile).
    if (quizEl && typeof quizEl.scrollIntoView === 'function') {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      quizEl.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  function updateQuizContent(q) {
    const quizEl = $('.tfq__quiz-screen', root);
    const panel = $('.tfq__quiz-panel', quizEl);

    // --- Animate question content swap ---
    const contentEl = $('.tfq__question-content', panel);
    contentEl.style.opacity = '0';
    contentEl.style.transform = 'translateY(10px)';

    setTimeout(() => {
      // Title
      const titleEl = $('.tfq__question-title', panel);
      titleEl.textContent = q.title;

      // Description
      const descEl = $('.tfq__question-desc', panel);
      descEl.textContent = q.desc || '';
      descEl.style.display = q.desc ? '' : 'none';

      // Multi-select counter
      const counterEl = $('.tfq__colour-counter', panel);
      if (q.type === 'multi') {
        const count = (state.answers[q.id] || []).length;
        counterEl.textContent = `${count} of ${q.maxSelect} selected`;
        counterEl.classList.remove('tfq__hidden');
      } else {
        counterEl.classList.add('tfq__hidden');
      }

      // Options — rebuild (they change per question)
      const optionsEl = $('.tfq__options', panel);
      optionsEl.className = `tfq__options${q.id === 'q3' ? ' tfq__options--colours' : ''}`;
      optionsEl.innerHTML = '';

      q.options.forEach(opt => {
        const selected = isOptionSelected(q, opt.value);
        const disabled = q.type === 'multi' && !selected && (state.answers[q.id] || []).length >= q.maxSelect;

        const optEl = el('div', {
          className: `tfq__option${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}${opt.img ? ' tfq__option--with-image' : ''}`,
          'data-value': opt.value,
          role: 'button',
          tabindex: disabled ? '-1' : '0',
          'aria-pressed': selected ? 'true' : 'false',
          onClick: () => selectOption(q, opt.value),
          onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOption(q, opt.value); } },
        });

        if (opt.img) {
          const imgWrap = el('div', { className: 'tfq__option-img-wrap' });
          imgWrap.appendChild(el('img', { className: 'tfq__option-img', src: opt.img, alt: opt.label }));
          optEl.appendChild(imgWrap);
        }

        const textWrap = opt.img ? el('div', { className: 'tfq__option-text-wrap' }) : optEl;

        if (q.id === 'q3' && opt.swatch) {
          textWrap.appendChild(el('div', { className: `tfq__colour-swatch tfq__colour-swatch--${opt.swatch}` }));
        }

        textWrap.appendChild(el('span', { className: 'tfq__option-label', textContent: opt.label }));

        if (opt.sublabel) {
          textWrap.appendChild(el('span', { className: 'tfq__option-sublabel', textContent: opt.sublabel }));
        }

        if (opt.img) {
          optEl.appendChild(textWrap);
        }

        optionsEl.appendChild(optEl);
      });

      // Fade content back in
      contentEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      contentEl.style.opacity = '1';
      contentEl.style.transform = 'translateY(0)';
    }, 150); // Short delay for fade-out

    // --- Update progress (no re-render, just update in-place) ---
    updateProgress(panel);
  }

  function updateProgress(panel) {
    // --- Trail breadcrumb (now lives inside .tfq__question-content, above the title) ---
    const contentEl = $('.tfq__question-content', panel);
    const trailEl = contentEl ? $('.tfq__progress-trail', contentEl) : null;
    if (trailEl) {
      trailEl.innerHTML = '';
      const visible = getVisibleQuestions();

      visible.forEach((q, idx) => {
        if (idx > state.currentStep) return;

        const ans = state.answers[q.id];

        if (idx > 0 && idx <= state.currentStep) {
          trailEl.appendChild(el('span', { className: 'tfq__progress-trail-sep', textContent: '›' }));
        }

        if (ans) {
          const label = Array.isArray(ans)
            ? ans.map(v => q.options.find(o => o.value === v)?.label || v).join(', ')
            : q.options.find(o => o.value === ans)?.label || ans;

          trailEl.appendChild(el('span', {
            className: idx === state.currentStep ? 'tfq__progress-trail-active' : 'tfq__progress-trail-step',
            textContent: label,
          }));
        } else if (idx === state.currentStep) {
          trailEl.appendChild(el('span', {
            className: 'tfq__progress-trail-active',
            textContent: q.key.charAt(0).toUpperCase() + q.key.slice(1),
          }));
        }
      });
    }

    // --- Circular ring progress ---
    const visible = getVisibleQuestions();
    const pct = getProgressPercent();
    const ringFill = $('.tfq__progress-ring-fill', panel);
    const pctText = $('.tfq__progress-pct', panel);
    const stepLabel = $('.tfq__progress-step-label', panel);

    if (ringFill) {
      const circumference = RING_CIRCUMFERENCE_DESKTOP; // fixed viewBox; SVG scales via CSS
      const offset = circumference - (pct / 100) * circumference;
      ringFill.style.strokeDashoffset = offset.toFixed(2);
    }
    if (pctText) pctText.textContent = pct + '%';
    if (stepLabel) stepLabel.textContent = `Step ${state.currentStep + 1} of ${visible.length}`;

    state.prevProgress = pct;

    // --- Nav buttons ---
    const navEl = $('.tfq__nav', panel);
    navEl.innerHTML = '';

    if (state.currentStep > 0) {
      navEl.appendChild(el('button', {
        className: 'tfq__btn tfq__btn--secondary',
        textContent: 'Back',
        onClick: goBack,
      }));
    }

    const nextBtn = el('button', {
      className: `tfq__btn tfq__btn--primary${!isCurrentAnswered() ? ' is-disabled' : ''}`,
      textContent: isLastStep() ? 'See Results' : 'Continue',
      onClick: goNext,
    });
    nextBtn.id = 'tfq-next-btn';
    navEl.appendChild(nextBtn);

    // Match counter badge next to Continue
    const matchBadge = el('span', {
      className: 'tfq__match-count',
      id: 'tfq-match-count',
      textContent: '',
    });
    navEl.appendChild(matchBadge);

    // Fetch live match count
    updateMatchCount();
  }

  // Debounced live match count
  let matchCountTimer = null;
  let lastMatchCount = null;

  function updateMatchCount() {
    if (matchCountTimer) clearTimeout(matchCountTimer);
    matchCountTimer = setTimeout(async () => {
      const badge = $('#tfq-match-count');
      if (!badge) return;
      try {
        const count = await fetchProducts([], { onlyUpToCurrentStep: true, countOnly: true });

        // Animate from previous count
        const prev = lastMatchCount ?? 0;
        animateMatchCounter(badge, prev, count, 400);
        lastMatchCount = count;

        badge.classList.toggle('tfq__match-count--zero', count === 0);
      } catch (e) {
        badge.textContent = 'All tiles';
      }
    }, 300);
  }

  function animateMatchCounter(element, from, to, duration) {
    const start = performance.now();
    const diff = to - from;
    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + diff * eased);
      element.textContent = current === 0 ? 'No matches' : `${current} tile${current !== 1 ? 's' : ''} match`;
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // renderProgress removed — replaced by updateProgress() above

  let currentQuizImage = null;

  function updateQuizImage(q) {
    const imgContainer = $('.tfq__quiz-image', root);
    if (!imgContainer) return;

    const targetImg = $(`[data-question-image="${q.id}"]`, imgContainer)
      || $('[data-question-image="default"]', imgContainer);

    if (!targetImg || targetImg === currentQuizImage) return;

    const imgs = $$('[data-question-image]', imgContainer);

    // Show target, hide others (remove display:none, use opacity for crossfade)
    imgs.forEach(img => {
      if (img === targetImg) {
        img.style.display = '';
        img.style.opacity = '1';
      } else {
        img.style.opacity = '0';
        img.style.display = 'none';
      }
    });

    currentQuizImage = targetImg;
  }

  function isOptionSelected(q, value) {
    const ans = state.answers[q.id];
    if (q.type === 'multi') return Array.isArray(ans) && ans.includes(value);
    return ans === value;
  }

  function selectOption(q, value) {
    if (q.type === 'multi') {
      let current = state.answers[q.id] || [];
      if (current.includes(value)) {
        current = current.filter(v => v !== value);
      } else {
        if (current.length >= q.maxSelect) return;
        current = [...current, value];
      }
      state.answers[q.id] = current;
    } else {
      state.answers[q.id] = value;
    }

    clearDependentAnswers(q.id);
    saveToSession();

    // Analytics — record the answer (key driver for option-popularity stats,
    // e.g. how many shoppers pick Kitchen vs Bathroom).
    trackEvent('tile_quiz_answer', {
      question_id: q.id,
      question: q.key,
      value: value,
      selected: isOptionSelected(q, value),
    });

    // Update options in-place (no full re-render)
    const quizEl = $('.tfq__quiz-screen', root);
    const optionsEl = $$('.tfq__option', quizEl);
    optionsEl.forEach(optEl => {
      const val = optEl.dataset.value;
      const selected = isOptionSelected(q, val);
      const disabled = q.type === 'multi' && !selected && (state.answers[q.id] || []).length >= q.maxSelect;

      optEl.classList.toggle('is-selected', selected);
      optEl.classList.toggle('is-disabled', disabled);
      optEl.setAttribute('aria-pressed', selected ? 'true' : 'false');
      optEl.setAttribute('tabindex', disabled ? '-1' : '0');
    });

    // Update multi-select counter
    if (q.type === 'multi') {
      const counterEl = $('.tfq__colour-counter', quizEl);
      if (counterEl) {
        const count = (state.answers[q.id] || []).length;
        counterEl.textContent = `${count} of ${q.maxSelect} selected`;
      }
    }

    // Update nav button state + progress trail
    const panel = $('.tfq__quiz-panel', quizEl);
    updateProgress(panel);
  }

  function isLastStep() {
    const visible = getVisibleQuestions();
    return state.currentStep >= visible.length - 1;
  }

  function goNext() {
    if (!isCurrentAnswered()) return;

    if (isLastStep()) {
      showLoadingAndFetchResults();
    } else {
      state.currentStep++;
      saveToSession();
      renderQuiz();
    }
  }

  function goBack() {
    if (state.currentStep > 0) {
      // Clear answers for steps after the one we're going back to
      const visible = getVisibleQuestions();
      for (let i = state.currentStep; i < visible.length; i++) {
        delete state.answers[visible[i].id];
      }
      state.currentStep--;
      lastMatchCount = null; // Reset so counter refreshes
      saveToSession();
      renderQuiz();
    }
  }

  /* ================================================================
     6. RESULTS & FILTERING
     ================================================================ */

  async function showLoadingAndFetchResults() {
    // Hide intro if visible
    const introEl = $('.tfq__intro', root);
    const howEl = $('.tfq__how', root);
    if (introEl) introEl.style.display = 'none';
    if (howEl) howEl.style.display = 'none';

    const loadingEl = $('.tfq__loading', root);
    if (loadingEl) loadingEl.classList.add('is-active');

    state.isLoading = true;

    try {
      const budgetFilter = state.answers['q10'];
      
      // Exclude budget filter initially to get all matches for other criteria
      let excludeFilters = [];
      if (budgetFilter && budgetFilter !== 'no-preference') {
        excludeFilters.push('q10');
      }

      let baseProducts = await fetchProducts(excludeFilters);
      
      // If no products match the base criteria, relax filters
      if (baseProducts.length === 0) {
        const relaxed = await progressiveRelaxation(excludeFilters);
        baseProducts = relaxed.products;
        state.relaxedFilters = relaxed.relaxedFilters;
      } else {
        state.relaxedFilters = [];
      }

      state.products = [];
      state.worthConsidering = [];

      if (budgetFilter && budgetFilter !== 'no-preference') {
        // Budget is matched against the price_band metafield (£/m²), not the raw
        // variant price (which is often £/tile or £/box). Products in the chosen
        // band are exact matches; the next band up is "worth considering".
        const BANDS = ['under-20', '20-30', '30-plus'];
        const nextBand = BANDS[BANDS.indexOf(budgetFilter) + 1] || null;

        baseProducts.forEach(p => {
          const band = p._facet && p._facet.pb;
          if (!band || band === budgetFilter) {
            // Exact band, or no band data (don't hide a tile just because it lacks a band).
            state.products.push(p);
          } else if (nextBand && band === nextBand) {
            state.worthConsidering.push(p);
          }
        });

        // If both are empty, relax everything (including budget)
        if (state.products.length === 0 && state.worthConsidering.length === 0) {
          const relaxed = await progressiveRelaxation();
          if (relaxed.products.length > 0) {
            state.relaxedFilters = relaxed.relaxedFilters;
            state.products = relaxed.products;
            state.worthConsidering = [];
          }
        }
      } else {
        // No budget constraint, everything is a direct match
        state.products = baseProducts;
        state.worthConsidering = [];
      }

      trackEvent('tile_quiz_complete', {
        matches: state.products.length,
        worth_considering: (state.worthConsidering || []).length,
        relaxed: state.relaxedFilters.length > 0,
        area: getAnswerLabel('q1'),
        budget: getAnswerLabel('q10'),
      });

      showResults();
    } catch (err) {
      showError();
    } finally {
      state.isLoading = false;
      const loadingEl = $('.tfq__loading', root);
      if (loadingEl) loadingEl.classList.remove('is-active');
    }
  }

  function buildFilterTags(options = {}) {
    const tags = [];
    const visible = getVisibleQuestions();
    // If onlyUpToCurrentStep is true, only include answers up to current step
    const maxIdx = options.onlyUpToCurrentStep ? state.currentStep : visible.length - 1;

    visible.forEach((q, idx) => {
      if (idx > maxIdx) return;

      const mapping = ATTRIBUTE_MAP[q.id];
      if (!mapping) return;

      const answer = state.answers[q.id];
      if (!answer) return;

      // Skip "no preference" answers
      if (mapping.skip && (
        (Array.isArray(answer) && answer.every(a => mapping.skip.includes(a))) ||
        (!Array.isArray(answer) && mapping.skip.includes(answer))
      )) return;

      if (mapping.type === 'tag') {
        if (Array.isArray(answer)) {
          // OR logic for multi-select (Q3 colours)
          tags.push({ qId: q.id, logic: 'OR', values: answer.map(v => mapping.prefix + v) });
        } else {
          tags.push({ qId: q.id, logic: 'AND', values: [mapping.prefix + answer] });
        }
      }
    });

    return tags;
  }

  /**
   * Fetch products from Shopify Storefront API.
   *
   * NOTE: This uses a tag-based query approach. The exact implementation
   * depends on how the in-house Shopify dev structures the product data.
   *
   * If the Storefront API token isn't available, falls back to /search.json
   * or /collections/all/products.json endpoint.
   */
  // ----------------------------------------------------------------
  // Catalogue matching
  //
  // The store tags products with descriptive marketing labels (e.g.
  // "Bathroom Wall Tiles", "Matt Floor Tiles", "Wood Effect Tiles") and keeps
  // colour in the product title — not the structured `area-`/`colour-` tags the
  // quiz originally queried. So instead of a server-side tag query, we load the
  // catalogue once and match each answer against the product's searchable text
  // (title + tags + description) and price. Adjust the keyword lists below to
  // tune matching; no Shopify re-tagging required.
  // ----------------------------------------------------------------
  // Keyword lists tuned to this store's real vocabulary (verified against the
  // product export: ~94% colour accuracy vs the colour_band metafield).
  const MATCH_KEYWORDS = {
    q1: { // Area — matched against full text (rooms are often only in the description)
      bathroom: ['bathroom'], kitchen: ['kitchen'], hallway: ['hall', 'hallway'],
      'living-room': ['living room', 'living area', 'lounge'], conservatory: ['conservatory'],
      'outside-garden': ['outdoor', 'garden', 'exterior', 'external', 'patio', 'paving', 'decking'],
      other: null,
    },
    q2: { // Pattern
      plain: ['plain'], 'marble-effect': ['marble', 'onyx'],
      'wood-effect': ['wood', 'oak', 'parquet', 'herringbone', 'plank'],
      'concrete-effect': ['concrete', 'cement', 'stone effect', 'stone-effect', 'travertine', 'terrazzo'],
      'split-face': ['split face', 'split-face', 'splitface', 'brick', 'slate'],
      'subtle-pattern': null,
      'strong-pattern': ['patterned', 'pattern', 'encaustic', 'geometric', 'moroccan', 'victorian', 'checker'],
      'no-preference': null,
    },
    q3: { // Colour (title + tags)
      white: ['white'],
      'beige-cream': ['beige', 'cream', 'taupe', 'buff', 'ivory', 'sand'],
      grey: ['grey', 'gray', 'charcoal', 'anthracite', 'graphite', 'dove', 'slate', 'silver', 'pearl', 'ash', 'zinc', 'iron', 'pewter', 'concrete'],
      red: ['red', 'burgundy', 'maroon', 'terracotta'],
      green: ['green', 'sage', 'emerald', 'olive', 'mint'],
      blue: ['blue', 'duck egg', 'navy', 'teal'],
      pink: ['pink', 'lilac', 'blush', 'rose'],
      brown: ['brown', 'honey', 'oak', 'copper', 'walnut', 'wood', 'coffee', 'mocha', 'tan', 'cotto', 'rust'],
      black: ['black', 'nero'],
      yellow: ['yellow', 'mustard', 'ochre', 'gold'],
      orange: ['orange', 'sunset'],
      multicoloured: ['multi', 'patchwork', 'checker', 'encaustic', 'moroccan', 'victorian', 'rainbow'],
    },
    q4: { // Look / size (title + tags)
      'large-rectified': ['large', 'extra large', 'rectified'], medium: ['medium'],
      small: ['small', 'metro', 'subway', 'brick'], mosaic: ['mosaic'], 'no-preference': null,
    },
    q6: { // Finish (title + tags)
      glossy: ['gloss', 'polished', 'metallic', 'lappato'], matt: ['matt', 'matte', 'satin'], 'no-preference': null,
    },
    q9: { // Shape (title + tags) — only an explicit hexagonal/fishscale request constrains
      rectangle: null, square: null, other: null, 'no-preference': null,
      hexagonal: ['hexagon', 'hex', 'fishscale', 'octagon'],
    },
  };

  // Pre-compile keyword lists to word-boundary regexes so "red" doesn't match
  // inside "Reduced", etc. Built once at load.
  const MATCH_REGEX = {};
  for (const qId of Object.keys(MATCH_KEYWORDS)) {
    MATCH_REGEX[qId] = {};
    for (const [opt, kws] of Object.entries(MATCH_KEYWORDS[qId])) {
      MATCH_REGEX[qId][opt] = kws
        ? kws.map(k => new RegExp('\\b' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i'))
        : null;
    }
  }
  function anyMatch(regexes, text) {
    return !!regexes && regexes.some(r => r.test(text));
  }

  /* ----------------------------------------------------------------
     Metafield → quiz-option mapping
     The store exposes structured metafields via the Storefront API. We map
     their controlled values to the quiz's option codes for exact matching,
     and fall back to the keyword lists above for any metafield not (yet)
     exposed or not set on a product.
     ---------------------------------------------------------------- */
  const MF_COLOUR = { beige: 'beige-cream', cream: 'beige-cream', black: 'black', blue: 'blue', brown: 'brown', 'golden brown': 'brown', green: 'green', grey: 'grey', lilac: 'pink', multi: 'multicoloured', pink: 'pink', red: 'red', 'rustic slate': 'grey', white: 'white', yellow: 'yellow' };
  const MF_PATTERN = { plain: 'plain', 'marble/onyx effect': 'marble-effect', 'wood effect': 'wood-effect', 'concrete/stone effect': 'concrete-effect', 'splitface/brick effect': 'split-face', patterned: 'strong-pattern' };
  const MF_SHAPE = { rectangle: 'rectangle', square: 'square', hexagon: 'hexagonal', fishscale: 'other', other: 'other' };
  const MF_SIZE = { 'extra large (90cm+)': 'large-rectified', 'large (55–89cm)': 'large-rectified', 'large (55-89cm)': 'large-rectified', 'medium (25–54cm)': 'medium', 'medium (25-54cm)': 'medium', 'small (under 25cm)': 'small' };
  const MF_PRICE = { 'up to £20 per sq m': 'under-20', '£20 - £30 per sq m': '20-30', '£30 plus per sq m': '30-plus' };
  // Anti-slip tiers, low→high. A product satisfies a request if its tier is at
  // least the requested one (an R11 tile is fine when the shopper asked "medium").
  const SLIP_RANK = { standard: 1, medium: 2, high: 3, 'very-high': 4 };

  // The option values each metafield can actually produce. A quiz answer OUTSIDE
  // this set (e.g. "subtle-pattern", "mosaic", "orange") has no metafield equivalent,
  // so we must keyword-match it even when the product has that facet.
  const COLOUR_SET = new Set(Object.values(MF_COLOUR));
  const PATTERN_SET = new Set(Object.values(MF_PATTERN));
  const SHAPE_SET = new Set(Object.values(MF_SHAPE));
  const SIZE_SET = new Set(Object.values(MF_SIZE));
  const FINISH_SET = new Set(['matt', 'glossy']);

  // Metafield values arrive JSON-encoded (e.g. '["Grey"]'). Return an array of strings, or null.
  function mfList(v) {
    if (v == null || v === '') return null;
    try { const a = JSON.parse(v); return Array.isArray(a) ? a.map(String) : [String(a)]; }
    catch (e) { return [String(v)]; }
  }
  function mfFinish(arr) {
    const t = arr.join(' ').toLowerCase();
    const o = [];
    if (/matt|satin/.test(t)) o.push('matt');
    if (/gloss|polished|metallic|lappato|sparkle/.test(t)) o.push('glossy');
    return o;
  }
  function mfR(arr) {
    const t = arr.join(' ').toUpperCase();
    if (/R13|R12/.test(t)) return 'very-high';
    if (/R11/.test(t)) return 'high';
    if (/R10/.test(t)) return 'medium';
    if (/R9|R8/.test(t)) return 'standard';
    return null;
  }
  function mfArea(suitArr, ext, tags) {
    const out = new Set();
    const src = ((suitArr || []).join(' ') + ' ' + (tags || []).join(' ')).toLowerCase();
    if (/bathroom|wet room/.test(src)) out.add('bathroom');
    if (/kitchen/.test(src)) out.add('kitchen');
    if (/\bhall/.test(src)) out.add('hallway');
    if (/living room/.test(src)) out.add('living-room');
    if (/conservatory/.test(src)) out.add('conservatory');
    if (/exterior|external|garden|outdoor|patio|paving|decking/.test(src) || ext === true) out.add('outside-garden');
    return [...out];
  }

  // Build the exact facet object from a product's metafields. Fields are only set
  // when the metafield is present, so the matcher can fall back to keywords otherwise.
  function buildFacet(mm, tags) {
    const f = {};
    const c = mfList(mm.colour_band);
    if (c) { const cs = [...new Set(c.map(x => MF_COLOUR[x.trim().toLowerCase()]).filter(Boolean))]; if (cs.length) f.c = cs; }
    const p = mfList(mm.pattern_effect);
    if (p && p.length) { const pp = MF_PATTERN[(p[0] || '').trim().toLowerCase()]; if (pp) f.p = pp; }
    const fin = mfList(mm.finish);
    if (fin) { const ff = mfFinish(fin); if (ff.length) f.f = ff; }
    const sh = mfList(mm.tile_shape);
    if (sh && sh.length) { const s = MF_SHAPE[(sh[0] || '').trim().toLowerCase()]; if (s) f.sh = s; }
    const sz = mfList(mm.tile_size_band);
    if (sz && sz.length) { const s = MF_SIZE[(sz[0] || '').trim().toLowerCase()]; if (s) f.sz = s; }
    const pb = mfList(mm.price_band);
    if (pb && pb.length) { const s = MF_PRICE[(pb[0] || '').trim().toLowerCase()]; if (s) f.pb = s; }
    const r = mfList(mm.anti_slip_r_rating);
    if (r) { const rr = mfR(r); if (rr) f.r = rr; }
    const w = mfList(mm.suitable_for_walls); if (w) f.w = /^yes/i.test(w[0] || '');
    const fl = mfList(mm.suitable_for_floors); if (fl) f.fl = /^yes/i.test(fl[0] || '');
    const ex = mfList(mm.suitable_for_external_use); if (ex) f.ex = /^yes/i.test(ex[0] || '');
    const u = mfList(mm.underfloor_heating); if (u) f.u = /underfloor heating/i.test(u.join(' '));
    const suit = mfList(mm.suitability); if (suit) { const a = mfArea(suit, f.ex, tags); if (a.length) f.a = a; }
    return f;
  }

  let _catalogCache = null;

  function normalizeRestProduct(p) {
    const variant = p.variants && p.variants[0];
    const image = p.images && p.images[0];
    const price = parseFloat(variant && variant.price || 0);
    const compareAt = parseFloat(variant && variant.compare_at_price || 0);
    const tags = Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(',') : []);
    const titleTags = `${p.title} ${tags.join(' ')}`.toLowerCase();
    const full = `${titleTags} ${(p.body_html || '').replace(/<[^>]+>/g, ' ')}`.toLowerCase();
    return {
      id: p.id,
      variantId: variant && variant.id || null,
      title: p.title,
      handle: p.handle,
      url: `/products/${p.handle}`,
      image: image && image.src || '',
      imageAlt: image && image.alt || p.title,
      images: (p.images || []).map(im => ({ src: im.src, alt: im.alt || p.title })),
      price: price,
      compareAtPrice: compareAt > price ? compareAt : null,
      currency: 'GBP',
      sku: variant && variant.sku || '',
      stockStatus: variant && variant.available === false ? 'out-of-stock' : 'in-stock',
      onSale: compareAt > price,
      pricePerSqm: null,
      pricePerBox: null,
      _text: titleTags, // title + tags — reliable for colour / finish / pattern / size / surface / shape
      _full: full,      // + description — used for area and underfloor heating
    };
  }

  // Metafield identifiers requested per product via the Storefront API.
  const MF_IDENTIFIERS = '[' + [
    'colour_band', 'pattern_effect', 'finish', 'tile_shape', 'tile_size_band', 'price_band',
    'anti_slip_r_rating', 'suitable_for_walls', 'suitable_for_floors', 'suitable_for_external_use',
    'suitability', 'underfloor_heating', 'sq_m_per_box', 'tiles_per_box',
  ].map(k => `{namespace:"custom",key:"${k}"}`).join(',') + ']';

  // Parse a numeric metafield value (may be plain "0.54" or JSON-wrapped '["0.54"]'). Returns a number or null.
  function mfNum(v) {
    if (v == null || v === '') return null;
    let x = v;
    try { const a = JSON.parse(v); x = Array.isArray(a) ? a[0] : a; } catch (e) { /* plain value */ }
    const n = parseFloat(x);
    return isNaN(n) ? null : n;
  }

  // Normalise a Storefront GraphQL product node into the same shape as
  // normalizeRestProduct, plus an exact `_facet` object built from metafields.
  function normalizeStorefrontNode(node) {
    // NB: the store's metafield keys are stored capitalised (e.g. "Colour_Band",
    // "Price_band"). The Storefront API matches our lowercase identifiers but
    // echoes back the canonical key, so index case-insensitively.
    const mm = {};
    (node.metafields || []).forEach(m => { if (m && m.key) mm[m.key.toLowerCase()] = m.value; });
    const tags = node.tags || [];
    const v = node.variants && node.variants.edges[0] && node.variants.edges[0].node;
    const img = node.images && node.images.edges[0] && node.images.edges[0].node;
    const price = parseFloat((v && v.price && v.price.amount) || 0);
    const compareAt = parseFloat((v && v.compareAtPrice && v.compareAtPrice.amount) || 0);
    let variantId = (v && v.id) || null;
    if (variantId && variantId.indexOf('/') !== -1) variantId = variantId.split('/').pop();
    let stock = 'in-stock';
    if (node.totalInventory === 0 || node.availableForSale === false) stock = 'out-of-stock';
    else if (typeof node.totalInventory === 'number' && node.totalInventory <= 10) stock = 'low-stock';
    const titleTags = `${node.title} ${tags.join(' ')}`.toLowerCase();
    const full = `${titleTags} ${(node.description || '').toLowerCase()}`;
    // Exact £/m² = per-tile price × tiles per box ÷ m² per box. Null when the metafields
    // aren't set/exposed — the card then falls back to the price band.
    const sqm = mfNum(mm.sq_m_per_box);
    const tpb = mfNum(mm.tiles_per_box);
    const pricePerSqm = (sqm > 0 && tpb > 0 && price > 0) ? (price * tpb / sqm) : null;
    return {
      id: node.id,
      variantId: variantId,
      title: node.title,
      handle: node.handle,
      // Relative path so links always resolve to the current (and, at go-live, the live) domain.
      url: `/products/${node.handle}`,
      image: (img && img.url) || '',
      imageAlt: (img && img.altText) || node.title,
      images: img ? [{ src: img.url, alt: img.altText || node.title }] : [],
      price: price,
      compareAtPrice: compareAt > price ? compareAt : null,
      currency: 'GBP',
      sku: (v && v.sku) || '',
      stockStatus: stock,
      onSale: compareAt > price,
      pricePerSqm: pricePerSqm,
      pricePerBox: null,
      _facet: buildFacet(mm, tags),
      _text: titleTags,
      _full: full,
    };
  }

  // Live catalogue via the Storefront API — reads structured metafields so
  // Colour / Pattern / Size / Finish / Budget etc. match exactly.
  async function fetchCatalogViaStorefront() {
    const url = `https://${QUIZ_CONFIG.storeDomain}/api/${QUIZ_CONFIG.storefrontApiVersion}/graphql.json`;
    const out = [];
    let cursor = null;
    let guard = 0;
    do {
      const after = cursor ? `, after: "${cursor}"` : '';
      const query = `{ products(first: 250${after}, query: "-tag:old-products") { pageInfo { hasNextPage endCursor } edges { node { id title handle tags availableForSale totalInventory onlineStoreUrl description(truncateAt: 300) images(first: 1) { edges { node { url altText } } } variants(first: 1) { edges { node { id sku availableForSale price { amount } compareAtPrice { amount } } } } metafields(identifiers: ${MF_IDENTIFIERS}) { key value } } } } }`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': QUIZ_CONFIG.storefrontToken },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
      const data = await res.json();
      if (data.errors) {
        console.error('[TFQ] Storefront API GraphQL errors:', JSON.stringify(data.errors));
        if (!data.data) throw new Error('Storefront API returned errors');
      }
      const conn = data.data && data.data.products;
      if (!conn) break;
      conn.edges.forEach(e => out.push(normalizeStorefrontNode(e.node)));
      cursor = conn.pageInfo.hasNextPage ? conn.pageInfo.endCursor : null;
      guard++;
    } while (cursor && guard < 10);
    return out;
  }

  // Fallback catalogue via the public /products.json endpoint (keyword matching only).
  async function fetchCatalogViaProductsJSON() {
    let all = [];
    let page = 1;
    const maxPages = 6; // safety limit (250 * 6 = 1500 products)
    while (page <= maxPages) {
      const res = await fetch(`/products.json?limit=250&page=${page}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Catalog fetch error: ${res.status}`);
      const data = await res.json();
      const batch = data.products || [];
      if (!batch.length) break;
      all = all.concat(batch);
      if (batch.length < 250) break;
      page++;
    }
    return all.map(normalizeRestProduct);
  }

  // Non-tile items (tools, adhesive, grout, sealant, panelling, heating kits) must
  // never surface as matches.
  const NON_TILE = [
    'tiling accessories', 'tiling tools', 'tiling preparation', 'floor levelling',
    'silicone sealant', 'grout', 'adhesive', 'trowel', 'notch', 'sealant',
    'tile preparation', 'cleaning aftercare', 'wall panelling', 'spacer',
    'heating kit', 'heating mat', 'thermostat', 'underfloor heating cable', 'insulation board',
  ];
  const TILE_WORD = /\b(tiles?|paving|slabs?|mosaic|porcelain|ceramic|vinyl|lvt|quarry|terrazzo|metro|subway|hexagon)\b/;
  function isTileProduct(p) {
    if (/\bsample\b/i.test(p.title)) return false;
    if (NON_TILE.some(t => p._text.includes(t))) return false;
    // Keep it only if it looks like a tile: has core tile metafields, or a tile word in title/tags.
    const core = p._facet && (p._facet.c || p._facet.p || p._facet.f || p._facet.sh || p._facet.sz);
    return !!(core || TILE_WORD.test(p._text));
  }

  async function fetchCatalog() {
    if (_catalogCache) return _catalogCache;
    const all = QUIZ_CONFIG.storefrontToken
      ? await fetchCatalogViaStorefront()
      : await fetchCatalogViaProductsJSON();
    _catalogCache = all.filter(isTileProduct);
    return _catalogCache;
  }

  // Match a product against one answer. Prefer the exact metafield facet when the
  // product exposes it; otherwise fall back to keyword matching (tri-state), so the
  // three metafields not yet exposed to the Storefront API still work.
  function productMatchesAnswer(p, qId, answer) {
    const fx = p._facet || null;
    switch (qId) {
      case 'q1': { // Area
        if (answer === 'other') return true;
        if (fx && fx.a) { // suitability metafield present → exact
          if (fx.a.indexOf(answer) !== -1) return true;
          if (answer === 'outside-garden' && fx.ex === true) return true;
          return false;
        }
        if (answer === 'outside-garden' && fx && fx.ex != null) return fx.ex === true;
        return anyMatch(MATCH_REGEX.q1[answer], p._full);
      }
      case 'q2': { // Pattern / effect
        if (fx && fx.p && PATTERN_SET.has(answer)) return fx.p === answer;
        const r = MATCH_REGEX.q2[answer];
        return r ? anyMatch(r, p._text) : true;
      }
      case 'q3': { // Colour — multi-select OR (per-value: facet if producible, else keyword)
        const vals = Array.isArray(answer) ? answer : [answer];
        return vals.some(v => (fx && fx.c && COLOUR_SET.has(v))
          ? fx.c.indexOf(v) !== -1
          : anyMatch(MATCH_REGEX.q3[v], p._text));
      }
      case 'q4': { // Size band
        if (fx && fx.sz && SIZE_SET.has(answer)) return fx.sz === answer;
        const r = MATCH_REGEX.q4[answer];
        return r ? anyMatch(r, p._text) : true;
      }
      case 'q5': { // Surface — wall / floor / both
        const wall = (fx && fx.w != null) ? !!fx.w : /\bwall\b/.test(p._text);
        const floor = (fx && fx.fl != null) ? !!fx.fl : /\bfloor\b/.test(p._text);
        if (answer === 'wall') return wall;
        if (answer === 'floor') return floor;
        if (answer === 'both') return wall && floor;
        return true;
      }
      case 'q6': // Finish
        if (fx && fx.f && FINISH_SET.has(answer)) return fx.f.indexOf(answer) !== -1;
        return anyMatch(MATCH_REGEX.q6[answer], p._text);
      case 'q7': // Anti-slip — exact when R-rating exposed; soft otherwise
        if (fx && fx.r) return SLIP_RANK[fx.r] >= (SLIP_RANK[answer] || 0);
        return true;
      case 'q8': // Underfloor heating — only constrain when the shopper needs it
        if (answer !== 'yes') return true;
        if (fx && fx.u != null) return fx.u === true;
        return /underfloor heating/.test(p._full);
      case 'q9': // Shape
        if (fx && fx.sh && SHAPE_SET.has(answer)) return fx.sh === answer;
        return answer === 'hexagonal' ? anyMatch(MATCH_REGEX.q9.hexagonal, p._text) : true;
      case 'q10': { // Budget — match on the price_band metafield; don't exclude tiles with no band
        if (answer === 'no-preference') return true;
        const band = fx && fx.pb;
        return !band || band === answer;
      }
      default:
        return true;
    }
  }

  async function fetchProducts(excludeFilters = [], options = {}) {
    const catalog = await fetchCatalog();
    const visible = getVisibleQuestions();
    const maxIdx = options.onlyUpToCurrentStep ? state.currentStep : visible.length - 1;

    // Collect the active answers (respecting visibility, relaxation and "no preference")
    const active = [];
    visible.forEach((q, idx) => {
      if (idx > maxIdx) return;
      if (excludeFilters.includes(q.id)) return;
      const answer = state.answers[q.id];
      if (answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0)) return;
      const mapping = ATTRIBUTE_MAP[q.id];
      if (mapping && mapping.skip) {
        const skipped = Array.isArray(answer)
          ? answer.every(a => mapping.skip.includes(a))
          : mapping.skip.includes(answer);
        if (skipped) return;
      }
      active.push({ qId: q.id, answer });
    });

    const matched = catalog.filter(p => active.every(a => productMatchesAnswer(p, a.qId, a.answer)));
    // The live "X tiles match" counter needs the TRUE total, not the display-capped page,
    // otherwise every filter with 30+ matches shows the same number.
    if (options.countOnly) return matched.length;
    return matched.slice(0, QUIZ_CONFIG.resultsPerPage);
  }

  async function fetchViaStorefrontAPI(andTags, orGroups) {
    // Build a product query filter string
    let queryParts = andTags.map(t => `tag:${t}`);

    // OR groups — wrap in parentheses
    orGroups.forEach(group => {
      if (group.length > 0) {
        const orPart = group.map(t => `tag:${t}`).join(' OR ');
        queryParts.push(`(${orPart})`);
      }
    });

    const queryString = queryParts.join(' AND ');

    const graphqlQuery = `
      {
        products(first: ${QUIZ_CONFIG.resultsPerPage}, query: "${escapeGraphQL(queryString)}") {
          edges {
            node {
              id
              title
              handle
              tags
              availableForSale
              totalInventory
              onlineStoreUrl
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 5) {
                edges {
                  node {
                    id
                    title
                    sku
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    availableForSale
                    quantityAvailable
                  }
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              metafield_price_per_sqm: metafield(namespace: "custom", key: "price_per_sqm") {
                value
              }
              metafield_price_per_box: metafield(namespace: "custom", key: "price_per_box") {
                value
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${QUIZ_CONFIG.storeDomain}/api/${QUIZ_CONFIG.storefrontApiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': QUIZ_CONFIG.storefrontToken,
      },
      body: JSON.stringify({ query: graphqlQuery }),
    });

    if (!response.ok) throw new Error(`Storefront API error: ${response.status}`);

    const data = await response.json();
    if (data.errors) {
      // Surface GraphQL errors instead of silently returning no products.
      console.error('[TFQ] Storefront API GraphQL errors:', data.errors, '\nQuery filter:', queryString);
      if (!data.data || !data.data.products) {
        throw new Error('Storefront API returned errors — see browser console for details.');
      }
    }

    const products = (data.data?.products?.edges || []).map(edge => normalizeProduct(edge.node));
    return products;
  }

  async function fetchViaProductsJSON(andTags, orGroups) {
    // Use public /products.json endpoint — no API token needed
    // Fetch all products and filter client-side by tags
    // Always use relative URLs to avoid cross-origin issues (adblockers, CORS)
    const baseUrl = '';

    let allProducts = [];
    let page = 1;
    const maxPages = 5; // Safety limit

    // Paginate through products (250 per page max)
    while (page <= maxPages) {
      const url = `/products.json?limit=250&page=${page}&_=${Date.now()}`;
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Products endpoint error: ${response.status}`);

      const data = await response.json();
      const batch = data.products || [];
      if (batch.length === 0) break;

      allProducts = allProducts.concat(batch);
      if (batch.length < 250) break;
      page++;
    }

    // Filter products by tags client-side
    const filtered = allProducts.filter(product => {
      // Handle tags as string (comma-separated) or array
      const rawTags = product.tags;
      const productTags = Array.isArray(rawTags)
        ? rawTags.map(t => t.toLowerCase().trim())
        : (typeof rawTags === 'string' ? rawTags.split(',').map(t => t.toLowerCase().trim()) : []);

      // AND logic: product must match ALL andTags
      for (const tag of andTags) {
        if (!productTags.includes(tag.toLowerCase())) return false;
      }

      // OR logic: product must match AT LEAST ONE tag in each OR group
      for (const group of orGroups) {
        if (group.length === 0) continue;
        const hasMatch = group.some(tag => productTags.includes(tag.toLowerCase()));
        if (!hasMatch) return false;
      }

      return true;
    });

    // Normalize into our product format
    return filtered.map(p => {
      const variant = p.variants?.[0];
      const image = p.images?.[0];
      const allImages = (p.images || []).map(img => ({ src: img.src || img, alt: img.alt || p.title }));
      const price = parseFloat(variant?.price || 0);
      const compareAt = parseFloat(variant?.compare_at_price || 0);

      return {
        id: p.id,
        variantId: variant?.id || null,
        title: p.title,
        handle: p.handle,
        url: `/products/${p.handle}`,
        image: image?.src || '',
        imageAlt: image?.alt || p.title,
        images: allImages,
        price: price,
        compareAtPrice: compareAt > price ? compareAt : null,
        currency: 'GBP',
        sku: variant?.sku || '',
        stockStatus: variant?.available === false ? 'out-of-stock' : 'in-stock',
        onSale: compareAt > price,
        pricePerSqm: null,
        pricePerBox: null,
      };
    });
  }

  async function fetchViaSearchEndpoint(andTags, orGroups) {
    // Simplified fallback using /search.json
    const allTags = [...andTags];
    orGroups.forEach(g => allTags.push(g[0])); // Just use the first of each OR group

    const query = allTags.join(' ');
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${QUIZ_CONFIG.resultsPerPage}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Search endpoint error: ${response.status}`);

    const data = await response.json();
    const products = (data.resources?.results?.products || []).map(p => ({
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: p.url,
      image: p.image,
      price: p.price,
      compareAtPrice: p.compare_at_price_max,
      sku: '',
      stockStatus: p.available ? 'in-stock' : 'out-of-stock',
      onSale: parseFloat(p.compare_at_price_max) > parseFloat(p.price),
      pricePerSqm: null,
      pricePerBox: null,
    }));

    return products;
  }

  function normalizeProduct(node) {
    const variant = node.variants?.edges?.[0]?.node;
    const image = node.images?.edges?.[0]?.node;
    const inventory = node.totalInventory;

    let stockStatus = 'in-stock';
    if (inventory === 0 || !node.availableForSale) stockStatus = 'out-of-stock';
    else if (inventory <= 10) stockStatus = 'low-stock';

    const price = parseFloat(variant?.price?.amount || node.priceRange?.minVariantPrice?.amount || 0);
    const compareAt = parseFloat(variant?.compareAtPrice?.amount || 0);

    // Extract numeric variant ID from GID
    let variantId = variant?.id || null;
    if (variantId && variantId.includes('/')) {
      variantId = variantId.split('/').pop();
    }

    return {
      id: node.id,
      variantId: variantId,
      title: node.title,
      handle: node.handle,
      url: node.onlineStoreUrl || `/products/${node.handle}`,
      image: image?.url || '',
      imageAlt: image?.altText || node.title,
      price: price,
      compareAtPrice: compareAt > price ? compareAt : null,
      currency: variant?.price?.currencyCode || 'GBP',
      sku: variant?.sku || '',
      stockStatus: stockStatus,
      onSale: compareAt > price,
      pricePerSqm: node.metafield_price_per_sqm?.value || null,
      pricePerBox: node.metafield_price_per_box?.value || null,
    };
  }

  function escapeGraphQL(str) {
    return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  async function progressiveRelaxation(initialExcludeFilters = []) {
    let relaxedFilters = [...initialExcludeFilters];
    for (const qId of RELAXATION_ORDER) {
      if (!relaxedFilters.includes(qId)) {
        relaxedFilters.push(qId);
      }
      const products = await fetchProducts(relaxedFilters);
      if (products.length > 0) {
        // Only return the ones we actually relaxed for logic reporting, exclude the ones we meant to exclude initially
        const reportingRelaxed = relaxedFilters.filter(f => !initialExcludeFilters.includes(f));
        return { products, relaxedFilters: reportingRelaxed };
      }
    }
    return { products: [], relaxedFilters: [] };
  }

  /* ================================================================
     7. RESULTS RENDERING
     ================================================================ */

  function showResults() {
    state.screen = 'results';
    const introEl = $('.tfq__intro', root);
    const quizEl = $('.tfq__quiz-screen', root);
    const resultsEl = $('.tfq__results', root);
    const howEl = $('.tfq__how', root);

    if (introEl) introEl.style.display = 'none';
    if (howEl) howEl.style.display = 'none';
    if (quizEl) quizEl.classList.remove('is-active');
    if (!resultsEl) return;

    resultsEl.classList.add('is-active');
    resultsEl.innerHTML = '';
    resultsEl.classList.add('tfq__fade-in');
    setTimeout(() => resultsEl.classList.remove('tfq__fade-in'), 400);

    const container = el('div', { className: 'page-width' });

    // Full-width layout — no image gallery
    const layout = el('div', { className: 'tfq__results-layout' });

    // Full-width results panel
    const panel = el('div', { className: 'tfq__results-panel' });

    // ---- Results Hero Header ----
    const hero = el('div', { className: 'tfq__results-hero' });

    const heroLeft = el('div', { className: 'tfq__results-hero-left' });
    heroLeft.appendChild(el('h1', {
      className: 'tfq__results-title',
      textContent: `${state.products.length} Tile${state.products.length !== 1 ? 's' : ''} Matched`,
    }));
    heroLeft.appendChild(el('p', {
      className: 'tfq__results-desc',
      textContent: 'Curated based on your preferences. Prices include VAT.',
    }));

    // Answer pills summary
    const pillsRow = el('div', { className: 'tfq__results-pills' });
    const visible = getVisibleQuestions();
    visible.forEach(q => {
      const ans = state.answers[q.id];
      if (!ans) return;
      const label = Array.isArray(ans)
        ? ans.map(v => q.options.find(o => o.value === v)?.label || v).join(', ')
        : q.options.find(o => o.value === ans)?.label || ans;
      pillsRow.appendChild(el('span', { className: 'tfq__results-pill', textContent: label }));
    });
    heroLeft.appendChild(pillsRow);
    hero.appendChild(heroLeft);
    panel.appendChild(hero);

    // Relaxed notice
    if (state.relaxedFilters.length > 0) {
      const relaxedNames = state.relaxedFilters.map(qId => {
        const q = QUESTIONS.find(qq => qq.id === qId);
        return q?.key || qId;
      }).join(', ');
      const notice = el('div', { className: 'tfq__relaxed-notice' }, [
        el('span', { innerHTML: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' }),
        el('span', { textContent: `We couldn't find an exact match — here are the closest tiles based on your other preferences. We adjusted: ${relaxedNames}.` }),
      ]);
      panel.appendChild(notice);
    }

    // ---- Products (card grid or empty state) ----
    if (state.products.length > 0 || (state.worthConsidering && state.worthConsidering.length > 0)) {
      const productsPerPage = 9;
      let visibleCount = Math.min(productsPerPage, state.products.length);
      
      const gridWrapper = el('div');
      
      // Results use the store's NATIVE product cards (rendered by the theme's
      // search section via renderProductListAsync), so card removal is handled by
      // event delegation on the wrapper — each native card's trash button carries
      // the tile handle in data-handle.
      gridWrapper.addEventListener('click', (e) => {
        const trashBtn = e.target.closest('.tfq__product-trash');
        if (trashBtn) {
          const handle = trashBtn.dataset.handle;
          if (handle) {
            state.products = state.products.filter(p => p.handle !== handle);
            const item = trashBtn.closest('.grid__item');
            if (item) item.remove();
            const resultsCountEl = $('.tfq__results-title', root);
            if (resultsCountEl) resultsCountEl.textContent = `${state.products.length} Tile${state.products.length !== 1 ? 's' : ''} Matched`;
            if (state.products.length === 0) showResults();
          }
        }
      });

      if (state.products.length > 0) {
        const mainGridContainer = el('div');
        gridWrapper.appendChild(mainGridContainer);
        mainGridContainer.innerHTML = '<div class="tfq__spinner" style="margin: 40px auto; display: block;"></div>';

        renderProductListAsync(visibleCount, state.products).then(grid => {
          mainGridContainer.innerHTML = '';
          mainGridContainer.appendChild(grid);

          // Load More button (if needed)
          if (state.products.length > productsPerPage) {
            const loadMoreBtn = el('button', {
              className: 'tfq__btn tfq__btn--secondary tfq__load-more',
              textContent: 'Load More',
              onClick: () => {
                visibleCount += productsPerPage;
                const oldText = loadMoreBtn.textContent;
                loadMoreBtn.textContent = 'Loading...';
                loadMoreBtn.disabled = true;

                renderProductListAsync(Math.min(visibleCount, state.products.length), state.products).then(newGrid => {
                  mainGridContainer.innerHTML = '';
                  mainGridContainer.appendChild(newGrid);
                  if (visibleCount < state.products.length) {
                    mainGridContainer.appendChild(loadMoreBtn);
                    loadMoreBtn.textContent = oldText;
                    loadMoreBtn.disabled = false;
                  }
                });
              }
            });
            mainGridContainer.appendChild(loadMoreBtn);
          }
        });
      } else {
        // If we only have worth considering, show the empty state first
        gridWrapper.appendChild(renderEmptyState());
      }

      // Worth Considering Section
      if (state.worthConsidering && state.worthConsidering.length > 0) {
        gridWrapper.appendChild(el('hr', { className: 'tfq__results-divider' }));
        gridWrapper.appendChild(el('h2', { className: 'tfq__results-worth-title', textContent: 'Worth Considering' }));
        gridWrapper.appendChild(el('p', { className: 'tfq__results-worth-desc', textContent: 'These tiles match your style but are slightly over your selected budget.' }));

        const worthGridContainer = el('div');
        gridWrapper.appendChild(worthGridContainer);
        worthGridContainer.innerHTML = '<div class="tfq__spinner" style="margin: 40px auto; display: block;"></div>';

        renderProductListAsync(state.worthConsidering.length, state.worthConsidering).then(worthGrid => {
          worthGridContainer.innerHTML = '';
          worthGridContainer.appendChild(worthGrid);
        });
      }
      
      panel.appendChild(gridWrapper);

      // Bottom buttons - All three centered
      const bottomActions = el('div', { className: 'tfq__results-bottom-actions' });
      bottomActions.appendChild(el('button', {
        className: 'tfq__btn tfq__btn--secondary',
        textContent: '← Refine Answers',
        onClick: () => { state.screen = 'quiz'; renderQuiz(); },
      }));
      bottomActions.appendChild(el('button', {
        className: 'tfq__btn tfq__btn--secondary',
        innerHTML: 'Share with a Friend',
        onClick: shareResults,
      }));
      bottomActions.appendChild(el('button', {
        className: 'tfq__btn tfq__btn--secondary',
        textContent: 'Start Over',
        onClick: restartQuiz,
      }));
      panel.appendChild(bottomActions);
    } else {
      panel.appendChild(renderEmptyState());
    }

    // Email capture
    panel.appendChild(renderEmailCapture());

    layout.appendChild(panel);
    container.appendChild(layout);
    resultsEl.appendChild(container);
    updateShareableURL();

    // Scroll to top
    window.scrollTo({ top: resultsEl.offsetTop - 20, behavior: 'smooth' });
  }

  function renderBreadcrumb() {
    const wrap = el('div', { className: 'tfq__breadcrumb' });
    const visible = getVisibleQuestions();

    visible.forEach((q, idx) => {
      const ans = state.answers[q.id];
      if (!ans) return;

      if (idx > 0) {
        wrap.appendChild(el('span', { className: 'tfq__breadcrumb-arrow', textContent: '→' }));
      }

      const label = Array.isArray(ans)
        ? ans.map(v => q.options.find(o => o.value === v)?.label || v).join(', ')
        : q.options.find(o => o.value === ans)?.label || ans;

      wrap.appendChild(el('span', { className: 'tfq__breadcrumb-item', textContent: label }));
    });

    return wrap;
  }

  function renderSummaryPanel() {
    const summary = el('div', { className: 'tfq__summary' });

    const toggle = el('button', { className: 'tfq__summary-toggle' });
    toggle.appendChild(el('h3', { textContent: 'Your Selections' }));
    toggle.innerHTML += `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    toggle.addEventListener('click', () => summary.classList.toggle('is-collapsed'));
    summary.appendChild(toggle);

    const list = el('ul', { className: 'tfq__summary-list' });
    const visible = getVisibleQuestions();

    visible.forEach(q => {
      const ans = state.answers[q.id];
      if (!ans) return;
      const label = Array.isArray(ans)
        ? ans.map(v => q.options.find(o => o.value === v)?.label || v).join(', ')
        : q.options.find(o => o.value === ans)?.label || ans;

      const item = el('li', {
        className: 'tfq__summary-item',
        innerHTML: `<strong>${q.key.charAt(0).toUpperCase() + q.key.slice(1)}:</strong> ${escapeHtml(label)}`,
      });
      list.appendChild(item);
    });

    summary.appendChild(list);
    return summary;
  }

  // Render results using the store's OWN native product cards. Instead of building
  // bespoke cards, we ask the theme to render its product-grid section for the matched
  // handles (/search?...&section_id=main-search), parse the returned HTML, and inject
  // the real .grid__item cards. This gives native styling, the official £/m² price,
  // sale badges, add-to-cart and Swym wishlist — all consistent with the rest of the
  // store. We overlay our own trash button (data-handle) so shoppers can remove a tile.
  // NOTE: this only works on the live store (the section endpoint + Dawn card markup
  // exist there); it can't render in the standalone local test harness.
  async function renderProductListAsync(limit, productsArray = state.products) {
    const productsToShow = limit ? productsArray.slice(0, limit) : productsArray;
    const grid = el('ul', { className: 'grid product-grid grid--4-col-desktop grid--2-col-tablet-down' });

    if (productsToShow.length === 0) return grid;

    const handles = productsToShow.map(p => `handle:${p.handle}`).join(' OR ');
    const url = `/search?type=product&q=${encodeURIComponent(handles)}&section_id=main-search`;

    try {
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');

      const items = Array.from(doc.querySelectorAll('#product-grid .grid__item'));
      const itemMap = {};

      items.forEach(item => {
        const link = item.querySelector('a.full-unstyled-link, a.card__media');
        if (link) {
          const href = link.getAttribute('href');
          const handleMatch = href.match(/\/products\/([^\?]+)/);
          if (handleMatch) {
            const handle = handleMatch[1];
            const inner = item.querySelector('.card__inner');
            if (inner) {
              const trashBtn = document.createElement('button');
              trashBtn.className = 'tfq__product-trash tfq__product-trash--overlay';
              trashBtn.type = 'button';
              trashBtn.setAttribute('aria-label', 'Remove from results');
              trashBtn.dataset.handle = handle;
              trashBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/><path d="M10 10v6"/><path d="M14 10v6"/></svg>';
              inner.appendChild(trashBtn);
            }
            itemMap[handle] = item;
          }
        }
      });

      // Preserve the quiz's match ordering.
      productsToShow.forEach(p => {
        if (itemMap[p.handle]) {
          grid.appendChild(itemMap[p.handle]);
        }
      });

      // Re-initialize Swym wishlists and other scripts if they exist.
      if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
        setTimeout(() => window._swat.initializeActionButtons(grid), 100);
      }

    } catch (e) {
      console.error('Error fetching rendered product cards', e);
    }

    return grid;
  }



  function renderEmptyState() {
    const empty = el('div', { className: 'tfq__empty' });
    
    // Message at the top
    empty.appendChild(el('div', { className: 'tfq__empty-icon', innerHTML: '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' }));
    empty.appendChild(el('h3', { textContent: 'No tiles found' }));
    empty.appendChild(el('p', { textContent: "We couldn't find tiles matching all your preferences. Try adjusting your choices or contact us for personalised help." }));
    
    // Buttons below
    const actions = el('div', { className: 'tfq__empty-actions' });
    actions.appendChild(el('button', {
      className: 'tfq__btn tfq__btn--secondary',
      textContent: '← Refine Answers',
      onClick: () => { state.screen = 'quiz'; renderQuiz(); },
    }));
    actions.appendChild(el('button', {
      className: 'tfq__btn tfq__btn--secondary',
      textContent: 'Start Over',
      onClick: restartQuiz,
    }));
    empty.appendChild(actions);

    return empty;
  }

  function renderEmailCapture() {
    const wrap = el('div', { className: 'tfq__email-capture' });

    const textWrap = el('div');
    textWrap.appendChild(el('h3', { textContent: 'Save your results' }));
    textWrap.appendChild(el('p', { textContent: 'Enter your email to receive a link to these results.' }));
    wrap.appendChild(textWrap);

    const form = el('form', { className: 'tfq__email-form' });
    form.addEventListener('submit', handleEmailSubmit);

    const input = el('input', {
      className: 'tfq__email-input',
      type: 'email',
      placeholder: 'your@email.com',
      required: 'required',
      'aria-label': 'Email address',
    });
    form.appendChild(input);

    form.appendChild(el('button', {
      className: 'tfq__btn tfq__btn--primary',
      type: 'submit',
      textContent: 'Send',
    }));

    wrap.appendChild(form);

    const success = el('p', { className: 'tfq__email-success', innerHTML: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Results sent to your email!' });
    wrap.appendChild(success);

    const error = el('p', { className: 'tfq__email-error', textContent: 'Something went wrong. Please try again.' });
    wrap.appendChild(error);

    return wrap;
  }

  /** Resolve a single answer to its human-readable label(s). */
  function getAnswerLabel(qId) {
    const q = QUESTIONS.find(x => x.id === qId);
    if (!q) return '';
    const ans = state.answers[qId];
    if (!ans) return '';
    return Array.isArray(ans)
      ? ans.map(v => q.options.find(o => o.value === v)?.label || v).join(', ')
      : q.options.find(o => o.value === ans)?.label || ans;
  }

  /** Make a product URL absolute (EmailJS templates need full links). */
  // Canonical public origin for email/share links. Prefer the configured store
  // domain so links never point at localhost or a theme-preview origin (which
  // are dead for the email recipient). Falls back to the current page origin.
  function siteOrigin() {
    const d = QUIZ_CONFIG.storeDomain;
    if (d && !/^(localhost|127\.0\.0\.1|0\.0\.0\.0)/i.test(d)) {
      return /^https?:\/\//i.test(d) ? d.replace(/\/+$/, '') : `https://${d}`;
    }
    return window.location.origin;
  }

  function toAbsoluteUrl(url) {
    if (!url) return siteOrigin();
    if (/^https?:\/\//i.test(url)) return url;
    return `${siteOrigin()}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  /** Map a product to the fields used by the EmailJS template loops. */
  function tileToEmailParam(p) {
    // Self-contained per-m² price string (matches the result card). The email template
    // should output {{price}} as-is — it already includes the £ and "/ m²" unit.
    let price = '';
    if (p.pricePerSqm) {
      price = `£${parseFloat(p.pricePerSqm).toFixed(2)} / m²`;
    } else if (p._facet && p._facet.pb) {
      const B = { 'under-20': 'Up to £20 / m²', '20-30': '£20–30 / m²', '30-plus': '£30+ / m²' };
      price = B[p._facet.pb] || '';
    }
    return {
      name: p.title || '',
      // Per-tile size/finish aren't stored on the product object, so we
      // reflect the shopper's selected preferences (what they were matched on).
      size: getAnswerLabel('q4'),
      finish: getAnswerLabel('q6'),
      price: price,
      product_url: toAbsoluteUrl(p.url),
      image_url: p.image || '',
    };
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const input = form.querySelector('input');
    const email = input.value.trim();
    if (!email) return;

    const btn = form.querySelector('button');
    const successEl = form.parentElement.querySelector('.tfq__email-success');
    const errorEl = form.parentElement.querySelector('.tfq__email-error');

    btn.textContent = 'Sending...';
    btn.disabled = true;
    errorEl.style.display = 'none';

    try {
      if (!window.emailjs || !QUIZ_CONFIG.emailServiceId || !QUIZ_CONFIG.emailTemplateId) {
        throw new Error('EmailJS is not configured.');
      }

      // Budget label already contains "/m²"; the template appends it, so strip it here.
      const budgetLabel = getAnswerLabel('q10').replace(/\s*\/\s*m²/gi, '');

      const recommended = (state.products || [])
        .slice(0, QUIZ_CONFIG.maxEmailRecommended)
        .map(tileToEmailParam);
      const worthConsidering = (state.worthConsidering || [])
        .slice(0, QUIZ_CONFIG.maxEmailWorthConsidering)
        .map(tileToEmailParam);

      const templateParams = {
        // Recipient — set the template's "To Email" field to {{to_email}} (or {{email}}).
        to_email: email,
        email: email,
        // Quiz summary block
        room: getAnswerLabel('q1'),
        style: getAnswerLabel('q2'),
        budget: budgetLabel,
        finish: getAnswerLabel('q6'),
        size: getAnswerLabel('q4'),
        // Tile lists (consumed by {{#recommended_tiles}} / {{#worth_considering}})
        recommended_tiles: recommended,
        worth_considering: worthConsidering,
        results_url: buildShareableURL(siteOrigin()),
      };

      await window.emailjs.send(
        QUIZ_CONFIG.emailServiceId,
        QUIZ_CONFIG.emailTemplateId,
        templateParams
      );

      form.style.display = 'none';
      successEl.style.display = 'block';
      trackEvent('tile_quiz_email', {
        matches: recommended.length,
        worth_considering: worthConsidering.length,
      });
    } catch (err) {
      // Surface the real reason — EmailJS rejects with { status, text }.
      const detail = err && (err.text || err.message) ? (err.text || err.message) : err;
      console.error('[TFQ] Email send failed:', err && err.status ? `(${err.status})` : '', detail);
      errorEl.textContent = 'Something went wrong. Please try again.';
      errorEl.style.display = 'block';
      btn.textContent = 'Send';
      btn.disabled = false;
    }
  }

  function showError() {
    const resultsEl = $('.tfq__results', root);
    const quizEl = $('.tfq__quiz-screen', root);
    const introEl = $('.tfq__intro', root);

    if (introEl) introEl.style.display = 'none';
    if (quizEl) quizEl.classList.remove('is-active');
    if (!resultsEl) return;

    resultsEl.classList.add('is-active');
    resultsEl.innerHTML = '';

    const container = el('div', { className: 'page-width' });
    const empty = el('div', { className: 'tfq__empty' });
    empty.appendChild(el('div', { className: 'tfq__empty-icon', innerHTML: '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' }));
    empty.appendChild(el('h3', { textContent: 'Something went wrong' }));
    empty.appendChild(el('p', { textContent: 'We had trouble loading your results. Please try again or contact us for help.' }));

    const actions = el('div', { className: 'tfq__empty-actions' });
    actions.appendChild(el('button', {
      className: 'tfq__btn tfq__btn--primary',
      textContent: 'Retry',
      onClick: showLoadingAndFetchResults,
    }));
    actions.appendChild(el('a', {
      className: 'tfq__btn tfq__btn--secondary',
      innerHTML: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> Contact Us',
      href: QUIZ_CONFIG.contactUrl,
    }));
    empty.appendChild(actions);

    container.appendChild(empty);
    resultsEl.appendChild(container);
  }

  /* ================================================================
     8. SHAREABLE URL
     ================================================================ */

  function buildShareableURL(origin) {
    const params = new URLSearchParams();
    Object.entries(state.answers).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        params.set(key, value.join(','));
      } else {
        params.set(key, value);
      }
    });
    // For email/share to an external origin (localhost preview, non-page path),
    // fall back to the known quiz page path so the link resolves on the store.
    let path = window.location.pathname;
    if (origin && (!path || path === '/' || /index\.html?$/i.test(path))) {
      path = '/pages/tile-finder';
    }
    return `${origin || window.location.origin}${path}?${params.toString()}`;
  }

  function updateShareableURL() {
    try {
      const url = buildShareableURL();
      window.history.replaceState({}, '', url);
    } catch (e) {
      // Silent fail — URL update is non-critical
    }
  }

  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    if (params.size === 0) return false;

    let hasQuizParams = false;
    QUESTIONS.forEach(q => {
      const val = params.get(q.id);
      if (val) {
        hasQuizParams = true;
        if (q.type === 'multi') {
          state.answers[q.id] = val.split(',').filter(Boolean);
        } else {
          state.answers[q.id] = val;
        }
      }
    });

    if (hasQuizParams) {
      state.screen = 'results';
      return true;
    }
    return false;
  }

  function shareResults() {
    const url = buildShareableURL();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        showToast('Link copied to clipboard!');
      }).catch(() => {
        fallbackCopy(url);
      });
    } else {
      fallbackCopy(url);
    }
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showToast('Link copied to clipboard!');
    } catch (e) {
      showToast('Copy this link: ' + text);
    }
    document.body.removeChild(ta);
  }

  function showToast(msg) {
    const existing = $('.tfq__toast');
    if (existing) existing.remove();

    const toast = el('div', {
      className: 'tfq__toast',
      textContent: msg,
    });
    toast.style.cssText = `
      position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
      background: var(--tfq-plum, #53364B); color: #fff; padding: 12px 24px;
      border-radius: 8px; font-size: 14px; z-index: 9999;
      animation: tfq-toast-in 0.3s ease forwards;
      font-family: 'Karla', sans-serif;
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  /* ================================================================
     9. SESSION PERSISTENCE
     ================================================================ */

  function saveToSession() {
    try {
      sessionStorage.setItem('tfq_state', JSON.stringify({
        screen: state.screen,
        currentStep: state.currentStep,
        answers: state.answers,
      }));
    } catch (e) {
      // Silent fail
    }
  }

  function restoreFromSession() {
    try {
      const saved = sessionStorage.getItem('tfq_state');
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed.answers && Object.keys(parsed.answers).length > 0) {
        state.screen = parsed.screen || 'quiz';
        state.currentStep = parsed.currentStep || 0;
        state.answers = parsed.answers;
      }
    } catch (e) {
      // Silent fail
    }
  }

  /* ================================================================
     10. ACTIONS
     ================================================================ */

  async function addAllToCart() {
    const btn = $('#tfq-add-to-cart');
    if (!btn) return;

    const availableProducts = state.products.filter(p => p.variantId && p.stockStatus !== 'out-of-stock');
    if (availableProducts.length === 0) {
      showToast('No available products to add.');
      return;
    }

    // Update button state
    const originalText = btn.innerHTML;
    btn.innerHTML = 'Adding to cart...';
    btn.classList.add('is-disabled');

    try {
      // Build items array for Shopify Cart API — use relative URL (same domain)
      const items = availableProducts.map(p => ({
        id: parseInt(p.variantId, 10),
        quantity: 1,
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || `Cart API error: ${response.status}`);
      }

      // Redirect to cart page
      window.location.href = '/cart';
    } catch (err) {

      btn.innerHTML = originalText;
      btn.classList.remove('is-disabled');
      showToast('Could not add to cart. Please try again.');
    }
  }

  function restartQuiz() {
    state = {
      screen: 'intro',
      currentStep: 0,
      answers: {},
      products: [],
      relaxedFilters: [],
      isLoading: false,
      prevProgress: 0,
    };
    quizScaffoldBuilt = false;
    sessionStorage.removeItem('tfq_state');

    // Clear URL params
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (e) {}

    renderIntro();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ================================================================
     11. UTILITIES
     ================================================================ */

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Emit an analytics event. Platform-agnostic — fires to whichever of these
   * the store has loaded, and always to a DOM event for custom listeners:
   *   - GA4         → window.gtag('event', name, params)
   *   - GTM         → window.dataLayer.push({ event: name, ...params })
   *   - Custom      → document dispatches 'tfq:<name>' with params in detail
   * No-ops silently if none are present, so it's safe to call anywhere.
   */
  function trackEvent(name, params = {}) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, params));
      }
      document.dispatchEvent(new CustomEvent('tfq:' + name, { detail: params }));
    } catch (e) {
      // Analytics must never break the quiz
    }
  }

  /* ================================================================
     12. INITIALIZATION
     ================================================================ */

  // Expose start function globally for the CTA button
  window.TileFinderQuiz = {
    start: startQuiz,
    restart: restartQuiz,
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
